import React from 'react';
import { AlertCircle, X, Clock } from 'lucide-react';

interface CompleteQuoteDialogProps {
  isOpen: boolean;
  additionalHours: number;
  totalHours: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const CompleteQuoteDialog: React.FC<CompleteQuoteDialogProps> = ({
  isOpen,
  additionalHours,
  totalHours,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Angebot als abgeschlossen markieren
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            <strong>Bist du sicher?</strong> Das Angebot wird als abgeschlossen markiert und eine Rechnung wird automatisch erstellt.
          </p>
          <p className="text-sm text-gray-600 mb-6">
            Nach dem Bestätigen kann das Angebot nicht mehr bearbeitet werden. Falls du noch Änderungen vornehmen möchtest, tue das jetzt! 😊
          </p>

          {/* Additional Hours Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 mb-2">
                  ⚠️ Alles Zusatzstunden erfasst?
                </p>
                {additionalHours > 0 ? (
                  <>
                    <p className="text-sm text-yellow-800 mb-2">
                      Es wurden <strong>{additionalHours}h zusätzliche Stunden</strong> zu diesem Angebot hinzugefügt.
                    </p>
                    <p className="text-sm text-yellow-800">
                      Die Rechnung wird mit <strong>{totalHours}h Gesamtstunden</strong> erstellt.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-yellow-800">
                    Bitte stelle sicher, dass alle zusätzlichen Stunden erfasst wurden, bevor du das Angebot abschließt.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-2">Zusammenfassung:</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Gesamtstunden:</span>
                <span className="text-gray-900 font-medium">{totalHours}h</span>
              </div>
              {additionalHours > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">davon Zusatzstunden:</span>
                  <span className="text-gray-900 font-medium">{additionalHours}h</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Ja, als abgeschlossen markieren ✓
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteQuoteDialog;

